import { useState, type FormEvent } from 'react';
import type { EntitlementMode } from '@economi/domain-contracts';
import type {
  DeveloperApiKeyItem,
  EntitlementState,
} from '../../services/solImpactEngine';
import { InfoPopover } from '../InfoPopover/InfoPopover';
import styles from './EntitlementAndApiView.module.css';

interface EntitlementAndApiViewProps {
  readonly entitlement: EntitlementState;
  readonly developerKeys: ReadonlyArray<DeveloperApiKeyItem>;
  readonly onToggleEntitlementMode: (mode: EntitlementMode) => void;
  readonly onGenerateApiKey: (params: {
    label: string;
    scopes: ReadonlyArray<string>;
  }) => void;
}

const AVAILABLE_SCOPES = [
  'kpi:read',
  'expenses:write',
  'inflows:write',
  'receipts:scan',
] as const;

export function EntitlementAndApiView({
  entitlement,
  developerKeys,
  onToggleEntitlementMode,
  onGenerateApiKey,
}: EntitlementAndApiViewProps) {
  const [testEmail, setTestEmail] = useState<string>('tercer.usuario.no.autorizado@gmail.com');
  const [keyLabel, setKeyLabel] = useState<string>('Integración Automatizada FinTech');
  const [selectedScopes, setSelectedScopes] = useState<string[]>([
    'kpi:read',
    'expenses:write',
  ]);

  const normalizedTestEmail = testEmail.trim().toLowerCase();
  const matchedPilot = entitlement.pilotAllowlist.find(
    (p) => p.email.toLowerCase() === normalizedTestEmail
  );

  const isAllowedInCurrentMode =
    entitlement.mode === 'SUBSCRIPTION_WITH_TRIAL' || Boolean(matchedPilot);

  const simulatedHttpPayload = isAllowedInCurrentMode
    ? matchedPilot
      ? {
          status: 200,
          entitlementMode: entitlement.mode,
          accessGranted: true,
          subscriptionStatus: 'PILOT_UNLIMITED',
          email: normalizedTestEmail,
          resendVerification: {
            provider: 'Resend Transactional Email API',
            otpVerified: true,
            lastOtpCode: matchedPilot.lastOtpCode,
          },
        }
      : {
          status: 201,
          entitlementMode: 'SUBSCRIPTION_WITH_TRIAL',
          accessGranted: true,
          subscriptionStatus: 'TRIALING',
          email: normalizedTestEmail,
          freeTrialDays: entitlement.trialDaysDuration,
          postTrialMonthlySoles: entitlement.monthlyPriceSoles,
          resendOnboardingSent: true,
        }
    : {
        type: 'https://api.economicontroller.pe/problems/pilot-email-not-authorized',
        title: 'Acceso Restringido a Marcha Blanca (2 Correos Autorizados)',
        status: 403,
        code: 'ERR_PILOT_EMAIL_NOT_AUTHORIZED',
        detail: `El correo '${normalizedTestEmail}' no pertenece a la lista cerrada de 2 cuentas de Marcha Blanca verificadas vía Resend.`,
        allowedPilotCount: 2,
      };

  const toggleScope = (scope: string) => {
    setSelectedScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
    );
  };

  const handleCreateKey = (e: FormEvent) => {
    e.preventDefault();
    if (!keyLabel.trim() || selectedScopes.length === 0) return;
    onGenerateApiKey({
      label: keyLabel.trim(),
      scopes: selectedScopes,
    });
    setKeyLabel('');
  };

  const activeKeyToken =
    developerKeys[0]?.fullTokenPreview ?? 'eco_live_9f8a7b6c5d4e3f2a1b0c9d8e7f6a5b4c';

  return (
    <div className={styles.grid}>
      {/* Columna Izquierda: Control de Acceso B2C (Marcha Blanca de 2 Correos -> Suscripción con Trial) */}
      <div className={styles.card}>
        <div>
          <h2 className={styles.title}>
            Control de Acceso B2C{' '}
            <InfoPopover title="Política de Acceso y Resend">
              Conmuta en caliente la política de <code>EntitlementPolicy</code> entre Fase 1 Marcha Blanca (bloqueo <code>403</code> salvo los 2 correos piloto verificados vía Resend) y Fase 2 Suscripción B2C con Free Trial.
            </InfoPopover>
          </h2>
        </div>

        <div className={styles.modeSwitchRow}>
          <button
            type="button"
            className={`${styles.modeOption} ${
              entitlement.mode === 'SOFT_LAUNCH_ALLOWLIST'
                ? styles.modeOptionActive
                : ''
            }`}
            onClick={() => onToggleEntitlementMode('SOFT_LAUNCH_ALLOWLIST')}
          >
            <span className={styles.modeTag}>FASE 1 · SOFT_LAUNCH_ALLOWLIST</span>
            <span className={styles.modeName}>Marcha Blanca (2 Correos)</span>
          </button>

          <button
            type="button"
            className={`${styles.modeOption} ${
              entitlement.mode === 'SUBSCRIPTION_WITH_TRIAL'
                ? styles.modeOptionActive
                : ''
            }`}
            onClick={() => onToggleEntitlementMode('SUBSCRIPTION_WITH_TRIAL')}
          >
            <span className={styles.modeTag}>FASE 2 · SUBSCRIPTION_WITH_TRIAL</span>
            <span className={styles.modeName}>
              Suscripción + {entitlement.trialDaysDuration}d Trial (S/{' '}
              {entitlement.monthlyPriceSoles.toFixed(2)}/mes)
            </span>
          </button>
        </div>

        <div className={styles.allowlistBox}>
          <strong style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
            LISTA ESTRICTA DE 2 CORREOS AUTORIZADOS EN MARCHA BLANCA (VERIFICADOS VÍA RESEND):
          </strong>
          {entitlement.pilotAllowlist.map((pilot) => (
            <div key={pilot.email} className={styles.allowlistRow}>
              <div>
                <div className={styles.emailText}>{pilot.email}</div>
                <div className={styles.roleSub}>
                  {pilot.roleLabel} · OTP Resend #{pilot.lastOtpCode}
                </div>
              </div>
              <span className={styles.verifiedPill}>✓ RESEND VERIFIED</span>
            </div>
          ))}
        </div>

        <div className={styles.testerBox}>
          <strong style={{ fontSize: '0.82rem' }}>
            Simulador de Verificación de Registro / Login (`POST /api/v1/auth/verify-access`)
          </strong>

          <div className={styles.quickEmailsRow}>
            <button
              type="button"
              className={styles.chipBtn}
              onClick={() => setTestEmail('alex.founder@economicontroller.pe')}
            >
              Probar Correo #1 Autorizado
            </button>
            <button
              type="button"
              className={styles.chipBtn}
              onClick={() => setTestEmail('piloto.vip@economicontroller.pe')}
            >
              Probar Correo #2 Autorizado
            </button>
            <button
              type="button"
              className={styles.chipBtn}
              onClick={() => setTestEmail('tercer.usuario.no.autorizado@gmail.com')}
            >
              Probar 3er Correo Externo
            </button>
          </div>

          <div className={styles.inputRow}>
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className={styles.input}
              aria-label="Correo a evaluar en el guardián B2C"
            />
          </div>

          <pre
            className={`${styles.responseConsole} ${
              isAllowedInCurrentMode ? styles.consoleAllowed : styles.consoleBlocked
            }`}
          >
            {JSON.stringify(simulatedHttpPayload, null, 2)}
          </pre>
        </div>
      </div>

      {/* Columna Derecha: Portal de Desarrolladores y API REST Pública (/api/v1/*) */}
      <div className={styles.card}>
        <div>
          <h2 className={styles.title}>
            API REST Desarrolladores (<code>/api/v1/*</code>){' '}
            <InfoPopover title="Autenticación X-API-Key e Idempotencia">
              Genera llaves personales (<code>eco_live_...</code>) con scopes granulares, cabecera <code>X-API-Key</code> e <code>Idempotency-Key</code> para conectar tus automatizaciones o bots.
            </InfoPopover>
          </h2>
        </div>

        <form
          onSubmit={handleCreateKey}
          style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}
        >
          <div className={styles.inputRow}>
            <input
              type="text"
              placeholder="Nombre de tu integración (ej. Script Python / iOS Shortcut)"
              value={keyLabel}
              onChange={(e) => setKeyLabel(e.target.value)}
              className={styles.input}
              required
            />
            <button type="submit" className={styles.testBtn}>
              + Generar API Key
            </button>
          </div>

          <div className={styles.scopesGrid}>
            {AVAILABLE_SCOPES.map((scope) => (
              <label key={scope} className={styles.scopeLabel}>
                <input
                  type="checkbox"
                  checked={selectedScopes.includes(scope)}
                  onChange={() => toggleScope(scope)}
                />
                <span>{scope}</span>
              </label>
            ))}
          </div>
        </form>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {developerKeys.map((keyItem) => (
            <div key={keyItem.id} className={styles.keyCard}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                }}
              >
                <span>{keyItem.label}</span>
                <span style={{ color: 'var(--accent-info)', fontFamily: 'var(--font-mono)' }}>
                  {keyItem.scopes.join(', ')}
                </span>
              </div>
              <code className={styles.tokenString}>{keyItem.fullTokenPreview}</code>
            </div>
          ))}
        </div>

        <div>
          <strong style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
            EJEMPLO LISTO DE CONSUMO API REST v1 CON IDEMPOTENCIA:
          </strong>
          <pre className={styles.responseConsole} style={{ marginTop: '0.45rem' }}>
            {`# 1. Consultar los 7 KPIs del Impacto Temporal del Sol
curl -X GET http://localhost:4000/api/v1/telemetry/kpis \\
  -H "X-API-Key: ${activeKeyToken}"

# 2. Registrar un gasto diario vía API Pública
curl -X POST http://localhost:4000/api/v1/expenses \\
  -H "X-API-Key: ${activeKeyToken}" \\
  -H "Idempotency-Key: exp-2026-10-15-001" \\
  -H "Content-Type: application/json" \\
  -d '{
    "amountSoles": 48.50,
    "category": "FOOD_DINING",
    "merchantName": "Café Bisetti Barranco",
    "description": "Reunión con cliente"
  }'`}
          </pre>
        </div>
      </div>
    </div>
  );
}
